// Globals
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Store
import { useAppDispatch, useAppState } from '@/store';
import { categoriesSelector } from '../../feature/selectors';
import { changeQuery, deleteFilter, getFilterData, getFilters } from '../../feature';

// Components
import {
  StyledContentWrapper,
  StyledPageTitle,
  StyledTableWrapper,
  StyledTabTitleWrapper,
} from '../../components/Styled';
import { Button, Drawer, Table } from '@/ui';
import { PlusCircleIcon } from '@/icons';
import { Pagination, showSuccessToast, useForm } from '@/components';
import DeleteModal from '../../components/DeleteModal/DeleteModal';
import FiltersTableFiltersContainer from '../FiltersTableFiltersContainer/FiltersTableFiltersContainer';
import FilterFormContainer from '../FilterFormContainer/FilterFormContainer';

// Models
import { IFilter } from '@/models/filters.model';

// Helpers
import { useModal } from '@/hooks';
import { getFiltersTableColumns } from './columns';
import GetFiltersDTO from '../../dtos/GetFiltersDTO';
import { Resource, usePermissions } from '@/modules/Permissions';

const FiltersTabContainer = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation(['categories']);
  const { canManage } = usePermissions();
  const canManageFilters = canManage(Resource.FILTER);
  const [form] = useForm();
  const { filters, filtersQuery } = useAppState(categoriesSelector);
  const { isOpened: isDrawerOpened, openModal: openDrawer, closeModal: closeDrawer } = useModal();
  const { isOpened: isDeleteModalOpen, openModal: openDeleteModal, closeModal: closeDeleteModal } = useModal();

  const [currentFilter, setCurrentFilter] = useState<IFilter | null>(null);

  const handlePageChange = (page: number) => {
    const dto = new GetFiltersDTO({
      ...filtersQuery,
      page,
    });

    dispatch(
      changeQuery({
        ...filtersQuery,
        page,
      }),
    );

    dispatch(getFilters(dto));
  };

  const handleDeleteClick = (filter: IFilter) => {
    setCurrentFilter(filter);
    openDeleteModal();
  };

  const handleEditClick = (filter: IFilter) => {
    setCurrentFilter(filter);
    dispatch(getFilterData(filter?.id));
    openDrawer();
  };

  const onDrawerClose = async () => {
    await form.resetFields();
    setCurrentFilter(null);
    closeDrawer();
  };

  const onCreate = () => {
    form.resetFields();
    openDrawer();
  };

  const handleDeleteFilter = async () => {
    try {
      if (currentFilter) {
        await dispatch(deleteFilter(currentFilter.id)).unwrap();
        setCurrentFilter(null);
        showSuccessToast({ message: t('delete_filter_success') });
      }
      closeDeleteModal();
    } catch {}
  };

  useEffect(() => {
    handlePageChange(1);
    return () => {
      dispatch(changeQuery({}));
    };
  }, []);

  return (
    <>
      <StyledTabTitleWrapper>
        <StyledPageTitle>{t('filters_tab_title')}</StyledPageTitle>

        {canManageFilters && (
          <Button onClick={onCreate} icon={<PlusCircleIcon style={{ maxWidth: '2rem', fill: '#000' }} />}>
            {t('create_new_filter')}
          </Button>
        )}
      </StyledTabTitleWrapper>

      <StyledContentWrapper>
        <StyledTableWrapper>
          <Table
            columns={getFiltersTableColumns({
              t,
              canManage: canManageFilters,
              onEdit: handleEditClick,
              onDelete: handleDeleteClick,
            })}
            dataSource={filters?.data}
            components={{
              header: {
                wrapper: FiltersTableFiltersContainer,
              },
            }}
          />
          <Pagination
            current={filters?.meta?.current_page ?? 1}
            total={filters?.meta?.total ?? 0}
            onChange={handlePageChange}
          />
        </StyledTableWrapper>
      </StyledContentWrapper>

      <Drawer open={isDrawerOpened} onClose={onDrawerClose}>
        {isDrawerOpened && <FilterFormContainer form={form} onSubmit={onDrawerClose} id={currentFilter?.id} />}
      </Drawer>

      <DeleteModal
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDeleteFilter}
        title={t('delete_filter_title', {
          name: currentFilter?.locale?.uk?.title ?? '',
        })}
        description={t('delete_filter_description')}
        closeBtnText={t('delete_filter_back')}
        confirmBtnText={t('delete_filter_confirm')}
      />
    </>
  );
};

export default FiltersTabContainer;
